/**
 * What the SLA panel says, as a value — WP-L5 (Task 53).
 *
 * Pure: no React, no `Date.now()` of its own, no clock. `nowMs` is passed in, for the
 * reason `web/src/overview/fold.ts` gives — an edge reachable only by mounting a
 * component is an edge nobody tests, and every interesting case here is an edge.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE ARITHMETIC IS THE SERVER'S. THIS MODULE DOES NOT REDO IT.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `sla_due_at` is `created_at + hours(severity)` from the company's active default
 * policy, and it is recomputed **from `created_at`** on a re-grade — so re-grading
 * buys no fresh budget, and an incident opened ten hours ago and upgraded to
 * `critical` (four hours) is immediately overdue. Nothing here recomputes any of
 * that: this module reads the instant the server sent.
 *
 * **`sla_breached` is the authority on whether the deadline was missed, not a
 * comparison performed here.** The server derives it as well as stamping it —
 * `sla_breached_at IS NOT NULL` **or** an overdue deadline in a non-concluding state
 * — because the sweep job that would stamp it is not built. Comparing timestamps in
 * the browser instead would report a live incident as overdue on a laptop whose clock
 * is fast, which is the same class of mistake as `Token panel`'s refusal to compute
 * expiry client-side. `nowMs` is used **only** to say *how far* past or short of the
 * deadline the incident is — a quantity nobody makes a decision on by itself — and
 * `overdue` is set by the flag, never by the subtraction.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  A REOPEN DOES NOT RESTART THE CLOCK, AND THE PANEL HAS TO SAY SO
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `resolved` and `false_positive` reopen into `investigating`, and the reopen keeps
 * the original `sla_due_at` **and** any breach. What it clears is the conclusion.
 *
 * So an incident that breached, was resolved, and was reopened comes back as
 * `state: 'investigating'`, `sla_breached: true`, `sla_due_at` still last Tuesday.
 * `describeSla` returns `{ kind: 'breached', running: true }` for it: **quá hạn**,
 * measured from the original deadline, with no fresh budget anywhere in the value.
 * `running` is what the screen keys the "the clock did not restart" sentence on, and
 * the sentence is rendered rather than merely not contradicted — an operator looking
 * at a live investigation will otherwise assume the deadline moved with it, and that
 * assumption is invisible while the screen is silent.
 */

import { isConcludingState, type IncidentState } from './state-machine';

/**
 * The three answers, kept apart because collapsing them is this build's oldest
 * mistake: an absent deadline is not a met one, and it is certainly not zero.
 *
 *  - `none`      — `sla_due_at` is `null`. The company's policy has an **explicit**
 *                  `null` for this severity, meaning *this severity has no deadline*.
 *                  Rendered as that sentence, never as "on time" and never as a
 *                  budget of zero.
 *  - `due`       — a deadline, not breached. `remainingMs` may be negative on a
 *                  concluding incident whose deadline passed after it concluded,
 *                  which the server does not count as a breach; the screen says
 *                  "closed after the deadline" rather than "overdue".
 *  - `breached`  — `sla_breached` is true. `overdueMs` is measured from the original
 *                  `sla_due_at` and from nothing else.
 */
export type SlaState =
  | { readonly kind: 'none' }
  | {
      readonly kind: 'due';
      readonly dueAt: string;
      readonly remainingMs: number;
      readonly running: boolean;
    }
  | {
      readonly kind: 'breached';
      readonly dueAt: string;
      readonly overdueMs: number;
      readonly running: boolean;
    };

export interface SlaInput {
  readonly slaDueAt: string | null;
  readonly slaBreached: boolean;
  readonly state: IncidentState;
}

/**
 * `running` — is this incident still being worked?
 *
 * `true` for every non-concluding state, which is exactly the set a reopen lands in.
 * It is not "was this reopened": the wire publishes no reopen flag, and inferring one
 * would be the client asserting a history it cannot read. What the panel needs to say
 * is true of both a first pass and a reopen — *the deadline is the original one* — so
 * it is keyed on the honest, published fact.
 */
export function describeSla(input: SlaInput, nowMs: number): SlaState {
  if (input.slaDueAt === null) return { kind: 'none' };
  const dueMs = Date.parse(input.slaDueAt);
  const running = !isConcludingState(input.state);

  // An unparseable instant is not silently treated as "no deadline": that would
  // render the loudest possible reassurance over a value the server did send. It is
  // reported as a breach only if the server says so, and otherwise as a deadline
  // with an unknown distance — `remainingMs: 0` would be a lie, so `NaN` propagates
  // and the renderer prints the instant without an interval.
  if (input.slaBreached) {
    return { kind: 'breached', dueAt: input.slaDueAt, overdueMs: nowMs - dueMs, running };
  }
  return { kind: 'due', dueAt: input.slaDueAt, remainingMs: dueMs - nowMs, running };
}

/**
 * A duration in whole hours and minutes, or `null` when there is nothing to say.
 *
 * `null` rather than `'0 phút'` when the input is not a finite number — an
 * unparseable timestamp, an arithmetic that produced `NaN`. Zero is a measurement and
 * "we could not measure" is not, and this build has already shipped one withheld
 * figure rendered as `0` (D-22). The renderer omits the interval entirely on `null`.
 */
export function splitDuration(
  ms: number,
): { readonly hours: number; readonly minutes: number } | null {
  if (!Number.isFinite(ms)) return null;
  const total = Math.floor(Math.abs(ms) / 60_000);
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}
